import { useEffect, useState, type JSX } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../../state/auth.store';
import { DuckMark } from './DuckMark';

/** The in-page anchors the nav scrolls to (smooth-scroll). Each id is set on its section. */
const LINKS = [
  { id: 'features', label: 'Features' },
  { id: 'how-it-works', label: 'How it works' },
  { id: 'gallery', label: 'Gallery' },
  { id: 'faq', label: 'FAQ' },
] as const;

/**
 * Sticky landing navbar. Transparent over the hero; gains GLASSMORPHISM (blur + semi-transparent bg +
 * hairline border/shadow) once the visitor scrolls past the fold. The "Create Room" CTA is ALWAYS visible
 * (desktop + mobile) — the one action we never want out of reach. The section links collapse into a
 * hamburger sheet on small screens; the CTA stays in the bar beside it.
 */
export function LandingNav(): JSX.Element {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  // Authed-aware CTA: a returning device gets "Enter lobby" → /lobby and no "Sign in" link (it's
  // already in); a logged-out visitor gets "Sign in" (→ /login) + the primary "Sign up" (→ /register).
  const authed = useAuthStore((s) => s.tokens !== null);

  // Glass-on-scroll. A passive scroll listener flipping a boolean is cheaper than a Framer useScroll
  // subscription for a single threshold, and reads clearly.
  useEffect(() => {
    const onScroll = (): void => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        scrolled
          ? 'border-b border-white/10 bg-[#0b1020]/70 shadow-[0_8px_30px_rgba(0,0,0,0.35)] backdrop-blur-xl'
          : 'border-b border-transparent'
      }`}
    >
      <nav
        aria-label="Primary"
        className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8"
      >
        {/* Wordmark */}
        <a href="#top" className="flex items-center gap-2 rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-amber-300/70">
          <DuckMark className="h-7 w-7" />
          <span className="text-base font-extrabold tracking-tight text-white">
            Duck Duck Space
          </span>
        </a>

        {/* Desktop section links */}
        <ul className="hidden items-center gap-1 md:flex">
          {LINKS.map((l) => (
            <li key={l.id}>
              <a
                href={`#${l.id}`}
                className="rounded-lg px-3 py-2 text-sm font-medium text-white/70 outline-none transition hover:text-white focus-visible:ring-2 focus-visible:ring-amber-300/70"
              >
                {l.label}
              </a>
            </li>
          ))}
        </ul>

        {/* Right cluster — the primary CTA is ALWAYS here. */}
        <div className="flex items-center gap-2 sm:gap-3">
          {!authed && (
            <Link
              to="/login"
              className="hidden rounded-lg px-3 py-2 text-sm font-medium text-white/70 outline-none transition hover:text-white focus-visible:ring-2 focus-visible:ring-amber-300/70 sm:inline-block"
            >
              Sign in
            </Link>
          )}
          <Link
            to={authed ? '/lobby' : '/register'}
            data-testid="cta-create-room"
            className="inline-flex items-center rounded-full bg-amber-400 px-4 py-2 text-sm font-bold text-slate-900 shadow-[0_4px_20px_rgba(255,210,63,0.35)] outline-none transition hover:scale-[1.03] hover:bg-amber-300 focus-visible:ring-2 focus-visible:ring-amber-200 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0b1020]"
          >
            {authed ? 'Enter lobby' : 'Sign up'}
          </Link>

          {/* Mobile hamburger — only the section links collapse here; the CTA above stays visible. */}
          <button
            type="button"
            aria-label="Menu"
            aria-expanded={menuOpen}
            aria-controls="mobile-nav-sheet"
            onClick={() => setMenuOpen((o) => !o)}
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-white/15 text-white/80 outline-none transition hover:bg-white/5 focus-visible:ring-2 focus-visible:ring-amber-300/70 md:hidden"
          >
            <span aria-hidden="true" className="text-lg leading-none">{menuOpen ? '✕' : '☰'}</span>
          </button>
        </div>
      </nav>

      {/* Mobile sheet of section links (CTA already lives in the bar above). */}
      {menuOpen && (
        <div
          id="mobile-nav-sheet"
          className="border-t border-white/10 bg-[#0b1020]/95 px-4 pb-4 backdrop-blur-xl md:hidden"
        >
          <ul className="flex flex-col py-2">
            {LINKS.map((l) => (
              <li key={l.id}>
                <a
                  href={`#${l.id}`}
                  onClick={() => setMenuOpen(false)}
                  className="block rounded-lg px-3 py-3 text-sm font-medium text-white/80 outline-none transition hover:bg-white/5 focus-visible:ring-2 focus-visible:ring-amber-300/70"
                >
                  {l.label}
                </a>
              </li>
            ))}
            {!authed && (
              <li>
                <Link
                  to="/login"
                  className="block rounded-lg px-3 py-3 text-sm font-medium text-white/80 outline-none transition hover:bg-white/5 focus-visible:ring-2 focus-visible:ring-amber-300/70"
                >
                  Sign in
                </Link>
              </li>
            )}
          </ul>
        </div>
      )}
    </header>
  );
}
